import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  type FormControlProps,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { isEmpty, isEqual, pickBy, toPairs } from "lodash-es";
import { type Dispatch, type FC, type SetStateAction, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { IntervalJson, OffsetUnit } from "../../../utils/interval";
import { offsetSentence } from "../../../utils/offsets";

export const OffsetSelector: FC<
  FormControlProps & {
    value: IntervalJson;
    onChangeInterval: (values: IntervalJson) => void;
  }
> = ({ value, onChangeInterval, ...props }) => {
  const { open, handleOpen, handleClose } = useDialog();

  const [offsets, setOffsets] = useState<[OffsetUnit, number][]>(
    isEmpty(value)
      ? []
      : (toPairs(pickBy(value, (v) => v !== 0 && v !== null && v !== undefined)) as [
          OffsetUnit,
          number,
        ][]),
  );

  useEffect(() => {
    onChangeInterval(offsets.reduce((acc, [unit, amount]) => ({ ...acc, [unit]: amount }), {}));
    // no dep: onChangeInterval isn't memoised and causes infinite loop; defined in cb so can't itself be a cb
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offsets]);

  if (offsets.length > 2) {
    toast.error("Only 2 offsets are supported");
    return null;
  }

  return (
    <>
      {offsets.length === 0 ? (
        <Button
          data-analytics-id="moment-form-offset-selector-open"
          disabled={props.disabled}
          onClick={handleOpen}
          variant="text"
          size="small"
        >
          Send this Communication before or after event
        </Button>
      ) : (
        <Grid sx={{ pl: 1 }}>
          {`Send Communication: ${offsetSentence(offsets)} event`}
          <Button
            data-analytics-id="moment-form-offset-remove"
            disabled={props.disabled}
            onClick={() => setOffsets([])}
          >
            <RemoveCircleOutline />
          </Button>
        </Grid>
      )}
      <Dialog open={open} onClose={handleClose} maxWidth="sm">
        <DialogTitle>Send before or after</DialogTitle>
        <DialogContent>
          {/* Can't pass a setstate reference to another component */}
          <OffsetModal
            offsets={offsets}
            setOffsets={(...args) => setOffsets(...args)}
            onClose={handleClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const OffsetModal: FC<
  FormControlProps & {
    offsets: [OffsetUnit, number][];
    setOffsets: Dispatch<SetStateAction<[OffsetUnit, number][]>>;
    onClose: () => void;
  }
> = ({ offsets, setOffsets, onClose, ...props }) => {
  if (offsets.length === 0) {
    setOffsets([["years", 1]]);
  }

  if (offsets.length === 1) {
    return (
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <OffsetForm
            value={offsets[0]!}
            onChangeOffset={(offset_) => setOffsets([[offset_[0], offset_[1]]])}
            first={true}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            data-analytics-id="moment-form-offset-dialog-add-another"
            disabled={props.disabled}
            onClick={() => {
              setOffsets([...offsets, [offsets[0]![0] === "days" ? "weeks" : "days", 1]]);
            }}
          >
            Add another rule
          </Button>
        </Grid>
        <Grid size={{ xs: 10 }}></Grid>
        <Grid size={{ xs: 2 }}>
          <Button
            data-analytics-id="moment-form-offset-dialog-ok"
            variant="outlined"
            onClick={onClose}
          >
            OK
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      {offsets.map((o, index) => (
        <Grid key={index} size={{ xs: 12 }}>
          <OffsetForm
            key={index}
            value={o}
            onChangeOffset={(offset_) => {
              setOffsets((current) => [
                ...current.slice(0, index),
                offset_,
                ...current.slice(index + 1),
              ]);
            }}
            exclude={offsets.filter((_, i) => i !== index).map(([unit]) => unit)}
            first={index === 0}
          />
          {index === 1 ? (
            <Button
              data-analytics-id="moment-form-offset-dialog-remove"
              disabled={props.disabled}
              onClick={() => {
                setOffsets((current) => current.slice(0, current.length - 1));
              }}
            >
              <RemoveCircleOutline />
            </Button>
          ) : null}
        </Grid>
      ))}
      <Grid size={{ xs: 10 }}></Grid>
      <Grid size={{ xs: 2 }}>
        <Button
          data-analytics-id="moment-form-offset-dialog-ok"
          variant="outlined"
          onClick={onClose}
        >
          OK
        </Button>
      </Grid>
    </Grid>
  );
};

const OffsetForm: FC<
  FormControlProps & {
    value: [OffsetUnit, number];
    onChangeOffset: (value: [OffsetUnit, number]) => void;
    exclude?: OffsetUnit[];
    first: boolean;
  }
> = ({ value, onChangeOffset, exclude, first, ...props }) => {
  const [offset, setOffset] = useState<[OffsetUnit, number]>(value);

  const [unit, amount] = offset;

  useEffect(() => {
    if (!isEqual(offset, value)) onChangeOffset(offset);
  }, [offset, value, onChangeOffset]);

  return (
    <>
      <FormControl {...props} sx={{ ...props.sx }}>
        <TextField
          sx={{ ml: 1, maxWidth: 100 }}
          value={Math.abs(amount)}
          variant="standard"
          disabled={props.disabled}
          onChange={(event) => {
            if (isNaN(Number.parseInt(event.target.value))) {
              setOffset((current) => [current[0], 0]);
            } else {
              setOffset((current) => [
                current[0],
                (current[1] < 0 ? -1 : 1) * Math.abs(Number.parseInt(event.target.value)),
              ]);
            }
          }}
          id={`schedule-date-number-input${first ? "" : "-1"}`}
        />
      </FormControl>
      <FormControl {...props} sx={{ ...props.sx }}>
        <Select
          value={unit}
          variant="standard"
          onChange={(event) => {
            setOffset((current) => [event.target.value, current[1]]);
          }}
        >
          {["days", "weeks", "months", "years"].map(
            (unit_) =>
              !(exclude ?? []).includes(unit_ as OffsetUnit) && (
                <MenuItem key={unit_} value={unit_}>
                  {unit_}
                </MenuItem>
              ),
          )}
        </Select>
      </FormControl>
      <FormControl {...props} sx={{ ...props.sx, minWidth: 130 }}>
        <Select
          labelId="offset-selector-label"
          value={amount < 0 ? -1 : 1}
          variant="standard"
          onChange={(event) => {
            setOffset((current) => [
              current[0],
              (event.target.value === 1 ? 1 : -1) * Math.abs(current[1]),
            ]);
          }}
          id={`schedule-date-offset-select${first ? "" : "-1"}`}
        >
          <MenuItem value={-1}>Before</MenuItem>
          <MenuItem value={1}>After</MenuItem>
        </Select>
      </FormControl>
    </>
  );
};
